import { ReactElement } from 'react';
import { Form } from 'react-bootstrap';
import { Checkbox, Radio } from '@Components/FormCheck';
import { DateInputField } from '@src/Components/DatePicker/DateInputField/DateInputField';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { MultiDropDownQuestion } from './MultiDropDownQuestion';
import { CheckOption, NestedDropdownProps, OnChange, OnChangeHelper } from './fiscalCalConstants';

interface NestedInputProps {
  config: NestedOptionConfig[];
  readOnly: boolean;
  onChange: OnChange;
  formState?: any;
  disabled?: boolean;
}

export const NestedInputType = {
  CHECKBOX: 'checkbox',
  DATEFIELD: 'datefield',
  DROPDOWN: 'dropdown',
  DROPDOWN_MULTI: 'dropdownMulti',
  RADIO: 'radio',
  CUSTOM: 'custom'
} as const;
export type NestedInputTypeValue = (typeof NestedInputType)[keyof typeof NestedInputType];

export interface NestedOptionConfig {
  inputType: NestedInputTypeValue;
  options?: { value: string | number | boolean; label: string }[];
  label?: string;
  placeholder?: string;
  formPath: string;
  note?: string;
  checkOptions?: CheckOption;
  onChangeHelper?: OnChangeHelper;
  disable?: boolean;
  dropdownProps?: NestedDropdownProps;
  customContent?: ReactElement;
  formState?: any;
  formSetter?: OnChange;
  subHeader?: string;
}

export type NestedOptionsConfigMap = Record<string, NestedOptionConfig[]>;

export const NestedInput = ({
  config,
  readOnly,
  onChange,
  formState,
  disabled = false
}: NestedInputProps) => (
  <div className='nested-fields'>
    {config.map(item => {
      let content;
      if (item.inputType === NestedInputType.CHECKBOX && item.options) {
        content = (
          <Form.Group key={`nestedFields-${item.inputType}`}>
            {item.options.map(option => {
              const { value, label } = option;
              const isChecked = item.checkOptions
                ? item.checkOptions(value, formState)
                : formState[item.formPath] === value;
              return (
                <Checkbox
                  key={`nestedFields-${item.inputType}-${value}`}
                  label={label}
                  checked={isChecked as boolean}
                  onChange={() => onChange(value, item.formPath, item.onChangeHelper)}
                  disabled={readOnly}
                />
              );
            })}
          </Form.Group>
        );
      } else if (item.inputType === NestedInputType.RADIO && item.options) {
        content = item.options.map((option, index) => {
          const { value, label } = option;
          const isChecked = formState[item.formPath] === value;
          return (
            <div key={`radioOption${index}`} className='radio-option'>
              <Radio
                key={`nestedFields-${item.inputType}-${value}`}
                name={item.label}
                id={`${item.label}-${value}-radio`}
                label={label}
                checked={isChecked}
                onChange={() => onChange(value, item.formPath, item.onChangeHelper)}
                disabled={readOnly}
              />
            </div>
          );
        });
      } else if (item.inputType === NestedInputType.DATEFIELD) {
        const currVal = formState[item.formPath];
        const isParseableDate = typeof currVal === 'string' && currVal.length === 10;
        content = (
          <DateInputField
            label={null}
            value={isParseableDate ? currVal : ''}
            invalidateDateOrRange={() => false}
            setValue={value => {
              onChange(value, item.formPath, item.onChangeHelper);
            }}
          />
        );
      } else if (item.inputType === NestedInputType.DROPDOWN && item.options) {
        const resolvedOnChange = item.formSetter ? item.formSetter : onChange;
        content = (
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            value={(() => {
              if (disabled || item.disable) return '';
              if (item.checkOptions) return item.checkOptions();
              return formState[item.formPath];
            })()}
            options={item.options}
            onChange={value => {
              resolvedOnChange(value, item.formPath, item.onChangeHelper);
            }}
            {...item.dropdownProps}
            disabled={disabled || item.disable}
            hasFilterBar={false}
          />
        );
      } else if (item.inputType === NestedInputType.DROPDOWN_MULTI && item.options) {
        const resolvedOnChange = item.formSetter ? item.formSetter : onChange;
        content = (
          <MultiDropDownQuestion
            options={item.options}
            onChange={value => {
              resolvedOnChange(value, item.formPath, item.onChangeHelper);
            }}
            checkOptions={item.checkOptions}
            formPath={item.formPath}
            onChangeHelper={item.onChangeHelper}
            formState={formState}
            dropdownProps={item.dropdownProps}
          />
        );
      } else if (item.inputType === NestedInputType.CUSTOM && item.customContent) {
        content = item.customContent;
      }
      return (
        content && (
          <div
            key={`nestedValue${item.label}${item.inputType}${item.formPath}`}
            className={item.disable ? 'disabled' : ''}
          >
            {item.label && <div className='nest-config-label'>{item.label}</div>}
            {item.subHeader && <div className='nest-config-label subheader'>{item.subHeader}</div>}
            {content}
            {item.note && <div className='nested-input-note'>{item.note}</div>}
          </div>
        )
      );
    })}
  </div>
);
